import { useDispatch } from 'react-redux';
import React, { useEffect, useRef, useState } from 'react';
import { HiDotsHorizontal } from "react-icons/hi";
import { FaCalendarDay, FaPercentage } from "react-icons/fa";
import { CustomLoader } from '../../../services/Reducer.js';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import flatpickr from 'flatpickr';
import 'flatpickr/dist/flatpickr.min.css';
import { FaIndianRupeeSign } from 'react-icons/fa6';
import { Autocomplete, TextField } from '@mui/material';

import apiClient from '../../../api/apiClient';
import authServices from '../../../services/AuthServices.js';

const ChargeSlabTable = () => {
  const inputRef = useRef(null);
  const [selectedDate, setSelectedDate] = useState([]);
  const [editableData, setEditableData] = useState({});
  const [modifiedRows, setModifiedRows] = useState(new Set());
  const [newRowCounter, setNewRowCounter] = useState(0);

  const [showModal, setShowModal] = useState(false);
  const [selectedInvoice2, setSelectedInvoice2] = useState(null);

  const toggleModal = (invoiceId) => {
    setSelectedInvoice2(invoiceId);
    setShowModal(!showModal);
  };

  // Edit modal state
  const [editModal, seteditModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  const toggleeditModal = (invoiceId) => {
    setSelectedInvoice(invoiceId);
    seteditModal(!editModal);
  };

  // State for charge slabs and vessel types
  const [chargeSlabs, setChargeSlabs] = useState([]);
  const [vesselTypes, setVesselTypes] = useState([]);

  const token = authServices.getToken();
  const dispatch = useDispatch();
  const loader = (display) => { dispatch(CustomLoader(display)) };

  // Fetch vessel types
  const fetchVesselTypes = async () => {
    try {
      loader('enable');
      const response = await apiClient.get("/api/admin/vessel_type/all", {
        headers: {
          'access_token': token,
        },
      });
      if (response.data?.Status === "SUCCESS") {
        setVesselTypes(response.data?.data || []);
      }
      loader('disable');
    } catch (err) {
      console.error('Error fetching vessel types:', err);
      loader('disable');
    }
  };

  // Fetch charge slabs
  const fetchChargeSlabs = async () => {
    try {
      loader('enable');
      const response = await apiClient.get("/api/admin/charge_slab/all", {
        headers: {
          'access_token': token,
        },
      });
      if (response.data?.Status === "SUCCESS") {
        setChargeSlabs(response.data?.data || []);
      }
      loader('disable');
    } catch (err) {
      console.error('Error fetching charge slabs:', err);
      loader('disable');
    }
  };

  // Initialize editable data
  useEffect(() => {
    if (chargeSlabs.length > 0) {
      const initialEditableData = {};
      chargeSlabs.forEach(item => {
        initialEditableData[item.id] = {
          ...item,
          vesselTypeName: item?.vesselTypeId?.name || ''
        };
      });

      // Preserve any newly added rows that haven't been saved yet
      setEditableData(prev => {
        const newEditableData = { ...initialEditableData };
        Object.keys(prev).forEach(key => {
          if (key.toString().startsWith('new-')) {
            newEditableData[key] = prev[key];
          }
        });
        return newEditableData;
      });

      setModifiedRows(new Set());
    } else {
      // If no records but we have unsaved new rows, keep them
      setEditableData(prev => {
        const newEditableData = {};
        Object.keys(prev).forEach(key => {
          if (key.toString().startsWith('new-')) {
            newEditableData[key] = prev[key];
          }
        });
        return newEditableData;
      });
    }
  }, [chargeSlabs]);

  // Handle input changes
  const handleInputChange = (id, field, value) => {
    setEditableData(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field]: value
      }
    }));

    // Add to modified rows
    setModifiedRows(prev => new Set(prev).add(id));
  };

  // Handle vessel type change
  const handleVesselTypeChange = (id, newValue) => {
    setEditableData(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        vesselTypeId: {
          id: newValue?.id || '',
          name: newValue?.name || ''
        }
      }
    }));

    // Add to modified rows
    setModifiedRows(prev => new Set(prev).add(id));
  };

  // Handle add new row
  const handleAddNewRow = () => {
    // Create a unique ID for the new row
    const newId = `new-${Date.now()}-${newRowCounter}`;
    setNewRowCounter(prev => prev + 1);

    const newRow = {
      id: newId,
      fromDays: '',
      toDays: '',
      charges: '',
      vesselTypeId: {
        id: '',
        name: ''
      }
    };

    setEditableData(prev => ({
      ...prev,
      [newId]: newRow
    }));

    // Add to modified rows
    setModifiedRows(prev => new Set(prev).add(newId));

    // Update chargeSlabs state to include the new row
    setChargeSlabs(prev => [...prev, { id: newId }]);
  };

  // Handle delete row
  const handleDeleteRow = (id) => {
    const idStr = id.toString();
    if (idStr.startsWith('new-')) {
      // For new rows, just remove them from state
      setEditableData(prev => {
        const newData = { ...prev };
        delete newData[id];
        return newData;
      });

      setModifiedRows(prev => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });

      setChargeSlabs(prev => prev.filter(item => item.id.toString() !== idStr));
    }
    // For database records, we don't do anything (button should be disabled)
  };

  // Handle save all changes
  const handleSaveAll = async () => {
    if (modifiedRows.size === 0) {
      toast.info("No changes to save");
      return;
    }

    try {
      loader('enable');

      // Prepare all modified rows
      const payload = Array.from(modifiedRows).map(id => {
        const rowData = editableData[id];
        return {
          fromDays: rowData.fromDays,
          toDays: rowData.toDays,
          charges: rowData.charges,
          vesselTypeId: { id: rowData.vesselTypeId.id },
        };
      });

      const response = await apiClient.post("/api/admin/charge_slab/bulkUpdate", payload, {
        headers: {
          'access_token': token,
        },
      });

      if (response.data?.Status === "SUCCESS") {
        toast.success(`Updated successfully`);
        // Reset new row counter
        setNewRowCounter(0);
        // Also clear editableData of any `new-` rows
        setEditableData({});
        // Clear modifiedRows
        setModifiedRows(new Set());
        // Re-fetch the data
        fetchChargeSlabs();
      } else {
        toast.error(response.data?.message || 'Failed to update records');
      }
    } catch (error) {
      console.error('Update error:', error);
      toast.error('Error updating records');
    } finally {
      loader('disable');
    }
  };

  // Date range picker initialization
  useEffect(() => {
    if (inputRef.current) {
      const fp = flatpickr(inputRef.current, {
        mode: "range",
        onChange: (selectedDates) => {
          if (selectedDates.length === 2) {
            setSelectedDate(selectedDates);
          }
        },
      });

      return () => {
        fp.destroy();
      };
    }
  }, []);


  // Initial data fetch
  useEffect(() => {
    fetchVesselTypes();
    fetchChargeSlabs();
  }, []);

  return (
    <>
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="row w-100 border-bottom-0 mt-4 p-2 ms-1">
              <div className="col-sm-12 col-md-12 col-lg-2 mb-2 d-flex justify-content-start align-items-start">
                <button
                  className="btn btn-light"
                  onClick={handleSaveAll}
                  disabled={modifiedRows.size === 0}
                >
                  Save&nbsp;All&nbsp;Changes
                </button>
              </div>

            </div>

            <div className="card-body text-center">
              <div style={{ overflowX: "auto" }}>
                <table
                  id="datatable"
                  className="table table-bordered nowrap w-100"
                >
                  <thead className='btn-light'>
                    <tr className="bg-transparent">
                      <th style={{ width: 100 }}>From Days</th>
                      <th style={{ width: 100 }}>To Days</th>
                      <th style={{ width: 100 }}>Charges</th>
                      <th style={{ width: 100 }}>Vessel Type</th>
                      <th style={{ width: 100 }}>Action</th>
                    </tr>
                  </thead>

                  <tbody>
                    {Object.values(editableData).length > 0 ? (
                      <>
                        {Object.values(editableData).map(item => (
                          <tr key={item.id} className={modifiedRows.has(item.id) ? 'table-warning' : ''}>
                            <td>
                              <input
                                type="number"
                                className="form-control"
                                value={item.fromDays || ''}
                                onChange={(e) => handleInputChange(item.id, 'fromDays', e.target.value)}
                              />
                            </td>
                            <td>
                              <input
                                type="number"
                                className="form-control"
                                value={item.toDays || ''}
                                onChange={(e) => handleInputChange(item.id, 'toDays', e.target.value)}
                              />
                            </td>
                            <td>
                              <input
                                type="number"
                                className="form-control"
                                value={item.charges || ''}
                                onChange={(e) => handleInputChange(item.id, 'charges', e.target.value)}
                              />
                            </td>
                            <td>
                              <Autocomplete
                                options={vesselTypes}
                                getOptionLabel={(option) => option.name || ''}
                                value={vesselTypes.find(type => type.id === item.vesselTypeId?.id) || null}
                                onChange={(event, newValue) => handleVesselTypeChange(item.id, newValue)}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    size="small"
                                    placeholder="Select Vessel Type"
                                  />
                                )}
                              />
                            </td>
                            <td>
                              <button
                                className="btn btn-danger btn-sm"
                                onClick={() => handleDeleteRow(item.id)}
                                disabled={typeof item.id !== 'string' || !item.id.startsWith('new-')}
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))}
                      </>
                    ) : (
                      <tr>
                        <td colSpan={5}>
                          <p className="text-center">Record Not Found</p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="d-flex justify-content-center mt-3">
                <button
                  className="btn btn-sm btn-light"
                  onClick={handleAddNewRow}
                >
                  Add New Slab
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ChargeSlabTable;